import List from '@pelckmans/elementis/components/list';
import Pill from '@pelckmans/elementis/components/pill';
import { useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Medialink } from '../../../api/medialink';
import { UserMaterial, UserMaterialBundleMember } from '../../../api/user-material';
import { buildGroupProps } from '../../../utils/elementisUtils';
import { BundleMaterialCacheContext, BundleMaterialCacheProvider } from '../context/bundle-material-cache';
import AddBundleMaterialButton from '../AddBundleMaterialButton';
import EmptyState from './Empty';
import { Module } from '../../../api/module';
import MaterialListItem from './MaterialListItem';
import { Material } from './materialUtils';

interface Props {
  materials: UserMaterialBundleMember[];
  module: Module;
  nodeId: string;
  onMaterialAdded: (material: UserMaterial) => void;
  onItemsChange: (items: (UserMaterial | Medialink)[]) => void;
  onOpenMaterial: (material: Material) => void;
  onDeleteMaterial: (material: Material) => void;
}

function MaterialList({
  materials,
  module,
  nodeId,
  onMaterialAdded,
  onItemsChange,
  onOpenMaterial,
  onDeleteMaterial,
}: Props) {
  const { t } = useTranslation();
  const { userMaterial, medialinks, fetchUserMaterial, fetchMedialinks } = useContext(BundleMaterialCacheContext);

  const items = useMemo(() => {
    return materials
      .map(m => {
        if (m.materialType === 'user-material') {
          return userMaterial[m.materialId];
        }
        return medialinks[m.materialId];
      })
      .filter(Boolean);
  }, [materials, userMaterial, medialinks]);

  useEffect(() => {
    const userMaterialIds = materials.filter(m => m.materialType === 'user-material').map(m => m.materialId);
    const medialinkIds = materials.filter(m => m.materialType === 'medialink').map(m => m.materialId);

    if (userMaterialIds.length > 0) fetchUserMaterial(userMaterialIds);
    if (medialinkIds.length > 0) fetchMedialinks(medialinkIds);
  }, [materials, fetchUserMaterial, fetchMedialinks]);

  useEffect(() => {
    onItemsChange(items);
  }, [items, onItemsChange]);

  return (
    <div className="list-material">
      {items.length === 0 ? (
        <EmptyState module={module} nodeId={nodeId} onMaterialAdded={onMaterialAdded} />
      ) : (
        <List density="compact">
          <List.Header
            title={t('SHARED_COMPONENTS.ADD_BUNDLE.MATERIAL_LIST.TITLE')}
            pill={<Pill>{materials.length}</Pill>}
            titleTag="h3"
            buttonGroupProps={buildGroupProps(
              <AddBundleMaterialButton module={module} nodeId={nodeId} onMaterialAdded={onMaterialAdded} />,
            )}
          />
          {items.map(m => (
            <MaterialListItem
              key={m.id}
              material={m}
              onOpenMaterial={onOpenMaterial}
              onDeleteMaterial={onDeleteMaterial}
            />
          ))}
        </List>
      )}
    </div>
  );
}

export default function MaterialListWithCache({
  materials,
  module,
  nodeId,
  onMaterialAdded,
  onItemsChange,
  onOpenMaterial,
  onDeleteMaterial,
}: Props) {
  return (
    <BundleMaterialCacheProvider>
      <MaterialList
        materials={materials}
        module={module}
        nodeId={nodeId}
        onMaterialAdded={onMaterialAdded}
        onItemsChange={onItemsChange}
        onOpenMaterial={onOpenMaterial}
        onDeleteMaterial={onDeleteMaterial}
      />
    </BundleMaterialCacheProvider>
  );
}
